import { debugDispatcher, isDebugDispatcherEnabled } from './debugDispatcher';

export type TCallback = (ev: KeyboardEvent) => unknown;

export type TKey = {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
};

export type TShortcutBranch = {
  key: TKey;
  children: TShortcutBranch[];
  callbacks: TCallback[];
  fireEvenFromInputs?: boolean;
};

export const shortcutController = new (class ShortcutController {
  history: TKey[] = [];

  candidates: TShortcutBranch[] = [];

  shortcuts: TShortcutBranch = {
    callbacks: [],
    children: [],
    key: { key: '' },
  };

  shortcutsStrings: string[] = [];

  categories = {
    dev: ['shift&D'],
  };

  constructor() {
    document.addEventListener('keydown', (ev) => {
      if (ev.key?.length !== 1 && ev.key !== 'Escape') return;
      this.candidates = [
        ...this.candidates.reduce<TShortcutBranch[]>(
          (accumulated, current) => [
            ...accumulated,
            ...current.children.filter(
              (child) =>
                child.key.key === ev.key &&
                child.key.altKey === ev.altKey &&
                child.key.ctrlKey === ev.ctrlKey &&
                child.key.shiftKey === ev.shiftKey,
            ),
          ],
          [],
        ),
        ...this.shortcuts.children.filter(
          (current) =>
            current.key.key === ev.key &&
            current.key.altKey === ev.altKey &&
            current.key.ctrlKey === ev.ctrlKey &&
            current.key.shiftKey === ev.shiftKey,
        ),
      ];
      this.candidates.forEach((current) => {
        if (
          current.fireEvenFromInputs ||
          !(
            !ev.key ||
            ev.key.length > 1 ||
            ['input', 'textarea', 'select'].includes(
              (ev.target as HTMLElement).tagName?.toLowerCase(),
            )
          )
        ) {
          if (current.callbacks) current.callbacks.forEach((cb) => cb(ev));
        }
      });
    });

    debugDispatcher.on(
      'shortcuts',
      () => {
        console.info(this.shortcutsStrings);
        console.info(this.shortcuts, this.history);
      },
      'Muestra los shortcuts registrados',
    );
    this.on(
      'short'.split(''),
      () => {
        this.shortcutsStrings.forEach((shortcut) => console.info(shortcut));
      },
      'dev',
    );
  }

  parseKeyToString(key: string | TKey) {
    if (typeof key === 'string') return key;

    return `${key.altKey ? 'alt&' : ''}${key.ctrlKey ? 'ctrl&' : ''}${
      key.shiftKey ? 'shift&' : ''
    }${key.key}`;
  }

  parseKey(keyString: string): TKey {
    const elements = keyString.split('&');
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const altKey = elements.includes('alt');
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const ctrlKey = elements.includes('ctrl');
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const shiftKey = elements.includes('shift');
    const key = elements.find(
      (current) =>
        current !== 'shift' && current !== 'alt' && current !== 'ctrl',
    );
    if (!key) throw new Error(`parseKey "${keyString}" does not have key.`);

    return {
      key,
      altKey,
      ctrlKey,
      shiftKey,
    };
  }

  /**
   * Para setear un shorcut se puede pasar un string representativo con la
   * forma:
   *
   * **alt**?&**ctrl**?&**shift**?&**(\w)**
   *
   * Donde: alt? ctrl? shift? implica que cualquiera de esas palabras pueden o
   * no aparecer y (\w) es una letra, símbolo o número.
   *
   * Puede aparecer cualquier tecla de control o no, pero el símbolo o letra
   * debe aparecer.
   *
   * @param category
   * Agrega un prefijo de teclas que se deben presionar antes del shortcut para
   * que funcione, de forma que por ejemplo, todos los shortcuts de la categoría
   * dev serán ejecutados solamente si antes se presionó shift&D
   *
   * @example
   *
    shortcutController.on(['shift&A', 'b', 'ctrl&c', 'alt&d'], (ev) => {
      ev.preventDefault();
      console.log('Abctrl+cd'),
    }); // Este shortcut se ejecuta en desarrollo y producción

    shortcutController.on('unshortcut'.split(''), (ev) => {
      ev.preventDefault();
      console.log('Abctrl+cd'),
    }, 'dev'); // Este shortcut solo se ejecuta en desarrollo
   */
  on(
    keys: (string | TKey)[],
    callback: TCallback,
    category?: keyof typeof this.categories,
    fireEvenFromInputs?: boolean,
  ) {
    if (category === 'dev' && !isDebugDispatcherEnabled()) return;

    let container = this.shortcuts;
    const actualKeys = category
      ? [...this.categories[category], ...keys]
      : keys;

    // eslint-disable-next-line no-restricted-syntax
    for (const key of actualKeys) {
      const actualKey = typeof key === 'string' ? this.parseKey(key) : key;
      if (actualKey.key === '')
        throw new Error(`Empty key ('') is not allowed`);
      const keyContainer = container.children.find(
        (current) =>
          current.key.key === actualKey.key ||
          (current.key.key === '' &&
            current.key.altKey === actualKey.altKey &&
            current.key.ctrlKey === actualKey.ctrlKey &&
            current.key.shiftKey === actualKey.shiftKey &&
            current.fireEvenFromInputs === fireEvenFromInputs),
      );
      if (keyContainer) container = keyContainer;
      else {
        const newContainer = {
          callbacks: [],
          children: [],
          key: actualKey,
          fireEvenFromInputs,
        };
        container.children.push(newContainer);
        container = newContainer;
      }
    }
    this.shortcutsStrings.push(
      actualKeys.map((current) => this.parseKeyToString(current)).join(''),
    );
    container.callbacks = [callback];
  }
})();
